// Chapter 1 - curated ChapterModule built from the hand-authored Chapter 1 content.
// This is the canonical depth reference for every other chapter.

import type { ChapterModule } from "./types";
import {
  SCIENCE_GOALS, ATTITUDES, HISTORY, BEHAVIOR_COMPARE, MENTALISM,
  BEHAVIORISMS, DIMENSIONS, TRAITS, ABA_DEFINITION, DOMAINS,
  GLOSSARY, QUIZ, EXAM_CHECKLIST, BACB_ALIGNMENT,
} from "../curriculum";

const chapter01Module: ChapterModule = {
  meta: {
    id: "01",
    number: 1,
    title: "Definition and Characteristics of Applied Behavior Analysis",
    subtitle: "Goals of science, attitudes, history, dimensions, definition, domains",
    part: "Part 1 - Introduction and Basic Concepts",
    estMinutes: 90,
    bacb: ["A-1", "A-2", "A-3", "A-4", "A-5"],
  },
  curated: true,
  overview: [
    "Chapter 1 lays the philosophical and historical foundation for the entire field. It defines applied behavior analysis (ABA) as a science derived from the experimental analysis of behavior and built on a specific set of attitudes and assumptions about how science is done.",
    "You will move from the three goals of science (description → prediction → control) through six attitudes that govern how behavior analysts work, the historical milestones from Watson and Skinner to the founding of JABA, the philosophical commitments of methodological vs radical behaviorism, the seven dimensions that define ABA practice (Baer, Wolf & Risley, 1968), and the four domains that scope the discipline.",
  ],
  objectives: BACB_ALIGNMENT.map(b => ({
    id: b.code,
    text: b.label,
  })),
  coreConcepts: [
    {
      title: "Three goals of science",
      body: [
        "Behavior analysts pursue three increasingly stringent goals: describe events, predict their co-occurrence with other events, and control them by manipulating environmental variables. Control - the demonstration of a functional relation - is the highest standard.",
      ],
      bullets: SCIENCE_GOALS.map(g => `${g.title} - ${g.one_liner}`),
    },
    {
      title: "Six attitudes of science",
      body: [
        "All scientists share a common set of assumptions and attitudes that shape how they study the world. Behavior analysts hold these especially explicitly:",
      ],
      bullets: ATTITUDES.map(a => `${a.title} - ${a.one_liner}`),
    },
    {
      title: "Respondent vs operant behavior",
      body: [
        "Behavior comes in two fundamentally different kinds: respondent (reflexive, elicited by an antecedent) and operant (emitted, selected by its consequences). The operant-consequence relation is the centerpiece of behavior analysis.",
      ],
      bullets: [
        ...BEHAVIOR_COMPARE.respondent.points.map((p: string) => `Respondent: ${p}`),
        ...BEHAVIOR_COMPARE.operant.points.map((p: string) => `Operant: ${p}`),
      ],
    },
    {
      title: "Mentalism and explanatory fictions",
      body: [
        MENTALISM.mentalism.body,
        MENTALISM.hypothetical_construct.body,
        MENTALISM.explanatory_fiction.body,
      ],
      bullets: [
        `Mentalism - ${MENTALISM.mentalism.body.split(".")[0]}.`,
        `Hypothetical construct - ${MENTALISM.hypothetical_construct.body.split(".")[0]}.`,
        `Explanatory fiction - ${MENTALISM.explanatory_fiction.body.split(".")[0]}.`,
      ],
    },
    {
      title: "Methodological vs radical behaviorism",
      body: [
        "Methodological behaviorism restricts the science to publicly observable behavior. Radical behaviorism - Skinner's philosophy - extends the same principles to private events (thinking, feeling) because they too are behavior.",
      ],
      bullets: [
        ...BEHAVIORISMS.methodological.points.map((p: string) => `Methodological: ${p}`),
        ...BEHAVIORISMS.radical.points.map((p: string) => `Radical: ${p}`),
      ],
    },
    {
      title: "Seven dimensions of ABA (Baer, Wolf & Risley, 1968)",
      body: [
        "Baer, Wolf, and Risley defined seven characteristics that distinguish ABA from related fields. Mnemonic: BAT CAGE.",
      ],
      bullets: DIMENSIONS.map(d => `${d.title} - ${d.one_liner}`),
    },
    {
      title: "Other ABA traits",
      body: ["Beyond the seven dimensions, ABA practice exhibits five additional traits."],
      bullets: TRAITS.map(t => `${t.title} - ${t.body.split(".")[0]}.`),
    },
    {
      title: "The definition of ABA",
      body: [
        ABA_DEFINITION.full,
        "It has four interlocking components: SCIENCE in which TACTICS derived from PRINCIPLES are applied SYSTEMATICALLY to improve SOCIALLY SIGNIFICANT behavior, with experimentation used to identify the variables responsible.",
      ],
      bullets: ABA_DEFINITION.parts.map(p => `${p.label} - ${p.hint}`),
    },
    {
      title: "Four domains of behavior analysis",
      body: ["Behavior analysis spans four domains from theoretical to applied:"],
      bullets: DOMAINS.map(d => `${d.title} (${d.province}) - Goal: ${d.goal}`),
    },
    {
      title: "Historical milestones",
      body: ["The field grew through key events from Watson's 1913 manifesto to the founding of JABA in 1968."],
      bullets: HISTORY.slice(0, 10).map((h: any) => `${h.year} - ${h.event}: ${h.detail}`),
    },
  ],
  glossary: GLOSSARY.map(g => ({ term: g.term, definition: g.long, example: g.short })),
  procedures: [
    {
      title: "Demonstrating a functional relation",
      intro: "Control - the highest goal of science - is achieved when a specific change in a dependent variable can be reliably produced by manipulating an independent variable, with confounds ruled out.",
      steps: [
        { title: "Operationally define the DV", detail: "Observable, measurable behavior with topographical and dimensional clarity." },
        { title: "Manipulate the IV", detail: "Introduce, remove, or vary the independent variable on a planned schedule." },
        { title: "Replicate the effect", detail: "Repeat the manipulation within and across subjects to rule out coincidence." },
        { title: "Rule out confounds", detail: "Hold extraneous variables constant; document and address threats to internal validity." },
        { title: "Report and disseminate", detail: "Make procedures and data public so others can replicate." },
      ],
    },
    {
      title: "Identifying which of the seven dimensions a study satisfies",
      intro: "Use the BAT CAGE checklist to evaluate whether a procedure meets each of the seven dimensions defined by Baer, Wolf, & Risley (1968).",
      steps: [
        { title: "Behavioral", detail: "Is the target an observable, measurable behavior - not a label or trait?" },
        { title: "Applied", detail: "Does the behavior matter to the participant or significant others?" },
        { title: "Technological", detail: "Are procedures specified in enough detail that any competent reader could replicate them?" },
        { title: "Conceptually systematic", detail: "Are tactics tied back to basic principles of behavior?" },
        { title: "Analytic", detail: "Is a functional relation demonstrated between procedure and behavior change?" },
        { title: "Effective", detail: "Is the change practically meaningful - not just statistically reliable?" },
        { title: "Generality", detail: "Does the change persist over time, across settings, or spread to related behaviors?" },
      ],
    },
  ],
  examples: [
    { scenario: "A teacher praises every student raised hand for one week and records hand-raising frequency.",
      application: "Demonstrates the APPLIED dimension - the behavior is socially significant and measured in the natural setting." },
    { scenario: "An analyst writes that a child 'lacks motivation' to explain low task completion.",
      application: "An explanatory fiction - 'motivation' is invoked as a cause but only re-labels the behavior. Behavior analysts seek environmental variables (e.g., reinforcer effectiveness, response effort) instead." },
    { scenario: "Tutoring procedure is fully described in JABA so other clinicians can replicate it.",
      application: "Demonstrates the TECHNOLOGICAL dimension - procedures specified in enough detail that a competent reader can carry them out." },
    { scenario: "A reading intervention improves scores during sessions and maintains gains 3 months later in a different classroom.",
      application: "Demonstrates GENERALITY - change persists across time and settings." },
    { scenario: "A token economy raises on-task behavior from 20% to 80% with a clear ABAB pattern.",
      application: "Demonstrates ANALYTIC - a functional relation is shown by reversing and reinstating the intervention." },
  ],
  comparisons: [
    {
      title: "Respondent vs Operant",
      left:  { label: "Respondent", points: BEHAVIOR_COMPARE.respondent.points as unknown as string[] },
      right: { label: "Operant",     points: BEHAVIOR_COMPARE.operant.points as unknown as string[] },
    },
    {
      title: "Methodological vs Radical Behaviorism",
      left:  { label: "Methodological", points: BEHAVIORISMS.methodological.points as unknown as string[] },
      right: { label: "Radical",         points: BEHAVIORISMS.radical.points as unknown as string[] },
    },
    {
      title: "Prediction vs Control",
      left:  { label: "Prediction", points: ["Based on observed correlations.", "Anticipates events but does not require manipulation.", "Cannot establish causation by itself."] },
      right: { label: "Control",     points: ["Requires controlled experimentation.", "Establishes a functional relation.", "The highest level of scientific understanding."] },
    },
  ],
  misconceptions: [
    {
      myth: "Behaviorism ignores thoughts and feelings.",
      reality: "Radical behaviorism treats private events (thinking, feeling) as behavior subject to the same principles, not as off-limits.",
    },
    {
      myth: "ABA is a single technique.",
      reality: "ABA is a science - its tactics are derived from principles of behavior and applied systematically; the field is much broader than any one procedure.",
    },
    {
      myth: "Determinism means humans have no choice.",
      reality: "Determinism is a working assumption that behavior is lawful and predictable from environmental variables - it does not deny experienced choice.",
    },
    {
      myth: "Correlation between an event and a behavior proves causation.",
      reality: "Only an experiment that manipulates the independent variable and rules out confounds demonstrates a functional (causal) relation.",
    },
  ],
  examChecklist: EXAM_CHECKLIST.map((c: { id: string; label: string }) => c.label),
  flashcardTerms: GLOSSARY.map(g => g.term),
  quiz: QUIZ.map((q: any) => {
    const letters = ["A", "B", "C", "D"];
    return {
      id: String(q.id).toUpperCase(),
      question: q.prompt,
      options: q.options.map((opt: string, i: number) => ({ label: letters[i], text: opt })),
      answer: letters[q.correct],
      explanation: q.explanation,
      topic: q.topic,
    };
  }),
  reviewPrompts: [
    "Explain in your own words why CONTROL is a stricter goal of science than prediction.",
    "Give an applied example of each of the six attitudes of science.",
    "Pick one of the seven dimensions and describe a study or procedure that exemplifies it.",
    "Why is 'because she has low motivation' considered an explanatory fiction?",
    "Distinguish methodological behaviorism from radical behaviorism with a concrete example of how each treats a private event.",
    "Walk through the four components of the formal definition of ABA (SCIENCE → TACTICS → PRINCIPLES → SOCIAL SIGNIFICANCE).",
    "Map each of Baer, Wolf, & Risley's seven dimensions to one of the four domains of behavior analysis.",
  ],
  recap: [
    "ABA is a SCIENCE in which TACTICS derived from PRINCIPLES of behavior are applied SYSTEMATICALLY to improve SOCIALLY SIGNIFICANT behavior, with experimentation used to identify the variables responsible for behavior change.",
    "The field rests on three goals of science (description, prediction, control), six attitudes (determinism, empiricism, experimentation, replication, parsimony, philosophic doubt), and seven dimensions (BAT CAGE: behavioral, applied, technological, conceptually systematic, analytic, generality, effective).",
    "Mastery of Chapter 1 means linking every concept above to concrete examples, distinguishing methodological from radical behaviorism, and being able to identify which dimension a given study satisfies.",
  ],
};

export default chapter01Module;
